import React, { useState, useEffect } from "react";
import { FaUserSecret, FaSignOutAlt, FaExclamationTriangle } from "react-icons/fa";

const ImpersonationBanner = ({ sidebarCollapsed = false }) => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [sidebarOffset, setSidebarOffset] = useState(0);

  useEffect(() => {
    const checkImpersonation = () => {
      const impersonating = localStorage.getItem("isImpersonating");
      const adminSession = localStorage.getItem("adminSession");

      if (impersonating === "true" && adminSession) {
        setIsImpersonating(true);
        try {
          const session = JSON.parse(adminSession);
          setAdminInfo(session);
        } catch (error) {
          console.error("Error parsing admin session:", error);
        }
      }
    };

    checkImpersonation();

    // Check token expiry and calculate time remaining
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiryTime = payload.exp * 1000; // Convert to milliseconds
        
        const updateTimer = () => {
          const now = Date.now();
          const remaining = Math.max(0, expiryTime - now);
          setTimeRemaining(remaining);

          if (remaining === 0) {
            handleEndImpersonation();
          }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
      } catch (error) {
        console.error("Error parsing token:", error);
      }
    }
  }, []);

  useEffect(() => {
    const updateOffset = () => {
      if (typeof window === "undefined") return;
      if (window.innerWidth >= 1024) {
        setSidebarOffset(sidebarCollapsed ? 80 : 288); // match lg:ml-20 and lg:ml-72
      } else {
        setSidebarOffset(0);
      }
    };

    updateOffset();
    window.addEventListener("resize", updateOffset);
    return () => window.removeEventListener("resize", updateOffset);
  }, [sidebarCollapsed]);

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleEndImpersonation = async () => {
    const adminSession = localStorage.getItem("adminSession");
    
    if (!adminSession) {
      alert("Admin session not found");
      return;
    }

    try {
      const session = JSON.parse(adminSession);
      const token = localStorage.getItem("token");

      // Log impersonation end
      if (session.impersonationLogId) {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/impersonation/end`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ impersonationLogId: session.impersonationLogId }),
        });
      }

      // Clear user session
      localStorage.removeItem("token");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userName");
      localStorage.removeItem("isImpersonating");

      // Restore admin session
      localStorage.setItem("adminToken", session.adminToken);
      localStorage.setItem("adminID", session.adminID);
      localStorage.removeItem("adminSession");

      // Redirect to admin dashboard
      window.location.href = process.env.REACT_APP_ADMIN_DASHBOARD_URL || "http://localhost:3000";
    } catch (error) {
      console.error("Error ending impersonation:", error);
      alert("Failed to end impersonation session");
    }
  };

  if (!isImpersonating) return null;

  return (
    <div
      className="fixed top-0 right-0 z-[60] bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg"
      style={{ left: sidebarOffset, width: `calc(100% - ${sidebarOffset}px)` }}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="hidden sm:flex items-center gap-3">
            <FaUserSecret className="w-5 h-5 animate-pulse" />
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <span className="font-semibold flex items-center gap-2">
                <FaExclamationTriangle className="w-4 h-4" />
                Admin Impersonation Mode
              </span>
              {adminInfo && (
                <span className="hidden sm:inline text-sm text-purple-100">
                  Logged in as user by admin: {adminInfo.adminID}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto">
            {timeRemaining !== null && (
              <span className="text-sm font-medium bg-purple-800 px-3 py-1 rounded-full">
                Session expires in: {formatTime(timeRemaining)}
              </span>
            )}
            <button
              onClick={handleEndImpersonation}
              className="flex items-center gap-2 px-4 py-1.5 bg-white text-purple-700 rounded-lg hover:bg-purple-50 transition-colors font-medium text-sm"
            >
              <FaSignOutAlt className="w-4 h-4" />
              Exit Impersonation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
